/**
 * 文件路径: D:/XXY/MeiShan_Project/frontend/shared/api/tenants.ts
 * 租户（美膳）组织与人员 API 服务
 * 
 * 重要提示：
 * 1. 部门API路径：/api/v1/admin/departments/ （不是 /api/v1/tenants/departments/）
 * 2. 员工API路径：/api/v1/users/?user_type=OPERATOR,OPERATOR_ADMIN （复用users API）
 * 3. 城市API路径：/api/v1/admin/cities/ 
 * 4. 遵循 httpClient 使用规范：使用 httpClient.get() 而不是 getAxiosInstance().get()
 * 
 * 更新记录：
 * - 2025-12-25: 添加 OperatorCity 接口和 listOperatorCities() 方法
 */

import { httpClient } from './http'

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

function toId(value: unknown): string | number {
  if (typeof value === 'string' || typeof value === 'number') return value
  return ''
}

function getNumberField(obj: Record<string, unknown>, key: string): number | undefined {
  const v = obj[key]
  return typeof v === 'number' ? v : undefined
}

/**
 * 运营商城市接口（用于企业归属城市选择）
 */
export interface OperatorCity {
  id: number
  name: string
  code: string
  /** 方案A：绑定国统码 city4（如 4401）；用于 allowed_cities/CITY 数据范围过滤 */
  admin_division_code?: string | null
  region?: number
  tenant?: number
  city_type?: 'TIER1' | 'TIER2' | 'TIER3' | 'OTHER'
  market_size?: 'LARGE' | 'MEDIUM' | 'SMALL'
  status?: 'ACTIVE' | 'INACTIVE'
  created_at?: string
  updated_at?: string
}

/**
 * 运营商区域接口（OperatorRegion）
 */
export interface OperatorRegion {
  id: number
  name: string
  code: string
  tenant?: number
  description?: string
  status?: 'ACTIVE' | 'INACTIVE'
  created_at?: string
  updated_at?: string
}

/**
 * 运营商租户（OperatorTenant）基础选项
 * API: GET /api/v1/admin/operator-tenants/
 */
export interface OperatorTenantOption {
  id: string | number
  name: string
  status?: string
}

/**
 * 获取运营商租户列表（用于顶部“选择租户”下拉）
 *
 * 注意：
 * - tenants 路由挂载在 /api/v1/admin/
 * - 该接口返回为分页结构（results/count），这里做归一化
 */
export async function listOperatorTenants(params?: {
  page?: number
  page_size?: number
  status?: string
}): Promise<OperatorTenantOption[]> {
  const response = await httpClient.get<unknown>('admin/operator-tenants/', {
    params: {
      page: params?.page ?? 1,
      page_size: params?.page_size ?? 200,
      ...(params?.status ? { status: params.status } : {}),
    },
  })

  if (isRecord(response) && Array.isArray((response as Record<string, unknown>).results)) {
    return ((response as Record<string, unknown>).results as unknown[]).filter(isRecord).map((r) => ({
      id: toId(r.id),
      name: String(r.name ?? ''),
      status: typeof r.status === 'string' ? r.status : undefined,
    }))
  }
  // 兼容极少数环境直接返回数组
  if (Array.isArray(response)) {
    return (response as unknown[]).filter(isRecord).map((r) => ({
      id: toId(r.id),
      name: String(r.name ?? ''),
      status: typeof r.status === 'string' ? r.status : undefined,
    }))
  }
  return []
}

/**
 * 获取运营商城市列表
 * API: GET /api/v1/admin/cities/
 */
export async function listOperatorCities(params?: {
  status?: 'ACTIVE' | 'INACTIVE'
  city_type?: string
  admin_division_code?: string
  page?: number
  page_size?: number
}): Promise<OperatorCity[]> {
  const response = await httpClient.get<unknown>('admin/cities/', {
    params: { 
      ...params,
      status: params?.status || 'ACTIVE',  // 默认只返回启用的城市
      page_size: params?.page_size || 100  // 默认获取全部（城市数量有限）
    } 
  })
  if (isRecord(response) && Array.isArray((response as Record<string, unknown>).results)) {
    return (response as { results: OperatorCity[] }).results || []
  }
  return Array.isArray(response) ? (response as OperatorCity[]) : []
}

/**
 * 获取运营商城市详情
 * API: GET /api/v1/admin/cities/{id}/
 */
export async function getOperatorCity(id: number): Promise<OperatorCity> {
  return await httpClient.get<OperatorCity>(`admin/cities/${id}/`)
}

/**
 * 获取运营商区域列表
 * API: GET /api/v1/admin/regions/
 */
export async function listOperatorRegions(params?: {
  status?: 'ACTIVE' | 'INACTIVE'
  page?: number
  page_size?: number
}): Promise<OperatorRegion[]> {
  const response = await httpClient.get<unknown>('admin/regions/', {
    params: {
      ...params,
      status: params?.status || 'ACTIVE',
      page_size: params?.page_size || 200,
    },
  })
  if (isRecord(response) && Array.isArray((response as Record<string, unknown>).results)) {
    return (response as { results: OperatorRegion[] }).results || []
  }
  return Array.isArray(response) ? (response as OperatorRegion[]) : []
}

/**
 * 创建运营商区域
 * API: POST /api/v1/admin/regions/
 */
export async function createOperatorRegion(payload: {
  name: string
  code: string
  description?: string
  tenant?: number
  status?: 'ACTIVE' | 'INACTIVE'
}): Promise<OperatorRegion> {
  return await httpClient.post<OperatorRegion>('admin/regions/', payload)
}

/**
 * 更新运营商区域
 * API: PATCH /api/v1/admin/regions/{id}/
 */
export async function patchOperatorRegion(
  id: number,
  payload: Partial<{
    name: string
    code: string
    description: string
    tenant: number
    status: 'ACTIVE' | 'INACTIVE'
  }>
): Promise<OperatorRegion> {
  return await httpClient.patch<OperatorRegion>(`admin/regions/${id}/`, payload)
}

/**
 * 删除运营商区域
 * API: DELETE /api/v1/admin/regions/{id}/
 */
export async function deleteOperatorRegion(id: number): Promise<void> {
  await httpClient.delete<unknown>(`admin/regions/${id}/`)
}

/**
 * 创建运营商城市
 * API: POST /api/v1/admin/cities/
 */
export async function createOperatorCity(payload: {
  name?: string
  code: string
  admin_division_code?: string | null
  region: number
  tenant?: number
  city_type?: string
  market_size?: string
  status?: 'ACTIVE' | 'INACTIVE'
}): Promise<OperatorCity> {
  return await httpClient.post<OperatorCity>('admin/cities/', payload)
}

/**
 * 更新运营商城市
 * API: PATCH /api/v1/admin/cities/{id}/
 */
export async function patchOperatorCity(
  id: number,
  payload: Partial<{
    name: string
    code: string
    admin_division_code: string | null
    region: number
    tenant: number
    city_type: string
    market_size: string
    status: 'ACTIVE' | 'INACTIVE'
  }>
): Promise<OperatorCity> {
  return await httpClient.patch<OperatorCity>(`admin/cities/${id}/`, payload)
}

/**
 * 删除运营商城市
 * API: DELETE /api/v1/admin/cities/{id}/
 */
export async function deleteOperatorCity(id: number): Promise<void> {
  await httpClient.delete<unknown>(`admin/cities/${id}/`)
}

/**
 * 租户部门接口
 */
export interface TenantDepartment {
  id: string | number
  tenant: string | number
  name: string
  parent: string | number | null
  level: number
  lft: number
  rght: number
  tree_id: number
  description?: string
  is_active: boolean
  user_count?: number
  children?: TenantDepartment[]
  created_at: string
  updated_at: string
}

/**
 * 租户员工接口
 */
export interface TenantStaff {
  id: string | number
  username: string
  display_name: string
  // 后端 admin/employee-positions 会返回 full_name（真实姓名）；用于选择器回显
  full_name?: string
  phone?: string
  email?: string
  department?: TenantDepartment
  department_id?: string | number
  position?: string
  position_id?: string | number
  position_level?: string
  position_category?: string
  position_level_display?: string
  position_category_display?: string
  operator_manager?: string | number | null
  operator_manager_name?: string
  assignment_type?: string
  assignment_type_display?: string
  status_display?: string
  employee_position_id?: string | number
  // 员工系统编号（对齐后端 employee_code 口径）
  employee_code?: string
  is_active: boolean
  created_at: string
  updated_at: string
}

/**
 * 租户部门 API
 * ⚠️ 注意：tenants API 实际挂载在 /api/v1/admin/ 路径下
 */
export class TenantDepartmentsApiService {
  /**
   * 获取部门树列表（根节点）
   * API: GET /api/v1/admin/departments/
   */
  async getDepartmentTree(): Promise<TenantDepartment[]> {
    const response = await httpClient.get<TenantDepartment[]>('admin/departments/')
    return Array.isArray(response) ? response : []
  }

  /**
   * 获取部门详情
   * API: GET /api/v1/admin/departments/{id}/
   */
  async getDepartment(id: string | number): Promise<TenantDepartment> {
    return await httpClient.get<TenantDepartment>(`admin/departments/${id}/`)
  }

  /**
   * 创建部门（用于部门管理入口）
   * API: POST /api/v1/admin/departments/
   */
  async createDepartment(data: {
    name: string
    parent?: string | number | null
    description?: string
    status?: string
  }): Promise<TenantDepartment> {
    return await httpClient.post<TenantDepartment>('admin/departments/', data)
  }

  /**
   * 更新部门（支持调整 parent，实现“运营部改为顶级部门”）
   * API: PATCH /api/v1/admin/departments/{id}/
   */
  async patchDepartment(id: string | number, data: Partial<{
    name: string
    parent: string | number | null
    description: string
    status: string
  }>): Promise<TenantDepartment> {
    return await httpClient.patch<TenantDepartment>(`admin/departments/${id}/`, data)
  }

  /**
   * 获取所有部门（扁平列表）
   * API: GET /api/v1/admin/departments/
   */
  async getAllDepartments(params?: Record<string, unknown>): Promise<TenantDepartment[]> {
    const response = await httpClient.get<unknown>('admin/departments/', { params })
    if (Array.isArray(response)) return response as TenantDepartment[]
    if (isRecord(response) && Array.isArray((response as Record<string, unknown>).results)) {
      return ((response as { results: TenantDepartment[] }).results || []) as TenantDepartment[]
    }
    return []
  }
}

/**
 * 运营商职级（OperatorPosition）接口
 * API: /api/v1/admin/positions/
 */
export interface OperatorPosition {
  id: string | number
  name: string
  code: string
  level: string
  category: string
  level_display?: string
  category_display?: string
  status_display?: string
  description?: string
  status?: string
  sort_weight?: number
  permissions?: Record<string, unknown>
  approval_permissions?: Record<string, unknown>
  data_access_permissions?: Record<string, unknown>
  created_at?: string
  updated_at?: string
}

/**
 * 职级枚举（由后端 categories 端点返回）
 * categories: [["OPERATIONS","运营序列"], ...]
 * levels: [["P1","P1-初级"], ...]
 */
export interface PositionEnums {
  categories: [string, string][]
  levels: [string, string][]
}

export class OperatorPositionsApiService {
  /**
   * 获取职级列表
   * API: GET /api/v1/admin/positions/
   */
  async list(params?: {
    status?: string
    level?: string
    category?: string
    search?: string
    page?: number
    page_size?: number
    ordering?: string
  }): Promise<{ results: OperatorPosition[]; count: number; page?: number; page_size?: number }> {
    const resp = await httpClient.get<unknown>('admin/positions/', { params })
    if (isRecord(resp) && Array.isArray((resp as Record<string, unknown>).results)) {
      const r = resp as Record<string, unknown> & { results: OperatorPosition[] }
      const count = getNumberField(r, 'count') ?? getNumberField(r, 'total') ?? 0
      const page = getNumberField(r, 'page')
      const pageSize = getNumberField(r, 'page_size')
      return {
        results: r.results || [],
        count,
        page,
        page_size: pageSize
      }
    }
    const arr = Array.isArray(resp) ? (resp as OperatorPosition[]) : []
    return { results: arr, count: arr.length }
  }

  /**
   * 更新职级配置（四块结构）
   * API: PATCH /api/v1/admin/positions/{id}/
   */
  async patch(
    id: string | number,
    data: Partial<Pick<OperatorPosition, 'permissions' | 'approval_permissions' | 'data_access_permissions'>>
  ): Promise<OperatorPosition> {
    return await httpClient.patch<OperatorPosition>(`admin/positions/${id}/`, data)
  }

  /**
   * 权限码预览
   * API: GET /api/v1/admin/positions/{id}/permission_preview/
   */
  async permissionPreview(id: string | number): Promise<{ permissions: string[]; data_access_permissions: Record<string, unknown> }> {
    const resp = await httpClient.get<unknown>(`admin/positions/${id}/permission_preview/`)
    if (!isRecord(resp)) return { permissions: [], data_access_permissions: {} }
    const perms = Array.isArray(resp.permissions) ? (resp.permissions as string[]) : []
    const cfg = isRecord(resp.data_access_permissions) ? (resp.data_access_permissions as Record<string, unknown>) : {}
    return { permissions: perms, data_access_permissions: cfg }
  }

  /**
   * 获取职级详情
   * API: GET /api/v1/admin/positions/{id}/
   */
  async retrieve(id: string | number): Promise<OperatorPosition> {
    return await httpClient.get<OperatorPosition>(`admin/positions/${id}/`)
  }

  /**
   * 创建职级
   * API: POST /api/v1/admin/positions/
   */
  async create(data: {
    name: string
    code: string
    level: string
    category: string
    description?: string
    status?: string
    sort_weight?: number
    permissions?: Record<string, unknown>
    approval_permissions?: Record<string, unknown>
    data_access_permissions?: Record<string, unknown>
  }): Promise<OperatorPosition> {
    return await httpClient.post<OperatorPosition>('admin/positions/', data)
  }

  /**
   * 删除职级
   * API: DELETE /api/v1/admin/positions/{id}/
   */
  async delete(id: string | number): Promise<void> {
    await httpClient.delete<unknown>(`admin/positions/${id}/`)
  }

  /**
   * 获取职级层级结构
   * API: GET /api/v1/admin/positions/hierarchy/
   */
  async hierarchy(): Promise<unknown> {
    return await httpClient.get<unknown>('admin/positions/hierarchy/')
  }

  /**
   * 获取职级统计信息
   * API: GET /api/v1/admin/positions/statistics/
   */
  async statistics(): Promise<unknown> {
    return await httpClient.get<unknown>('admin/positions/statistics/')
  }

  /**
   * 获取职级类别和等级枚举（动态，后端驱动）
   * API: GET /api/v1/admin/positions/categories/
   * 返回: { categories: [["OPERATIONS","运营序列"],...], levels: [["P1","P1-初级"],...] }
   */
  async categories(): Promise<PositionEnums> {
    const resp = await httpClient.get<unknown>('admin/positions/categories/')
    if (!isRecord(resp)) return { categories: [], levels: [] }
    const r = resp as Record<string, unknown>
    const cats = Array.isArray(r.categories) ? (r.categories as [string, string][]) : []
    const lvls = Array.isArray(r.levels) ? (r.levels as [string, string][]) : []
    return { categories: cats, levels: lvls }
  }

  /**
   * 获取可审批的职级列表
   * API: GET /api/v1/admin/positions/{id}/approvable_positions/
   */
  async approvablePositions(id: string | number): Promise<unknown> {
    return await httpClient.get<unknown>(`admin/positions/${id}/approvable_positions/`)
  }
}

/**
 * 职级变更申请接口
 * API: /api/v1/admin/position-change-requests/
 */
export interface OperatorPositionChangeRequest {
  id: string | number
  requester?: string | number
  requester_name?: string
  target_employee: string | number
  target_employee_name?: string
  change_type: 'PROMOTION' | 'DEMOTION' | 'TRANSFER' | 'NEW_ASSIGNMENT'
  current_position?: string | number
  current_position_info?: { position_name?: string; department_name?: string; level?: string } | null
  target_department?: string | number
  target_department_name?: string
  target_position: string | number
  target_position_name?: string
  target_position_level?: string
  assignment_type?: string
  effective_from?: string
  effective_until?: string | null
  reason?: string
  status: 'DRAFT' | 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED'
  approver?: string | number
  approver_name?: string
  approved_at?: string
  approval_notes?: string
  attachments?: unknown
  created_at?: string
  updated_at?: string
}

export class OperatorPositionChangeRequestsApiService {
  /**
   * 获取变更申请列表
   * API: GET /api/v1/admin/position-change-requests/
   */
  async list(params?: {
    status?: string
    change_type?: string
    page?: number
    page_size?: number
  }): Promise<{ results: OperatorPositionChangeRequest[]; count: number }> {
    const resp = await httpClient.get<unknown>('admin/position-change-requests/', { params })
    if (isRecord(resp) && Array.isArray((resp as Record<string, unknown>).results)) {
      const r = resp as Record<string, unknown> & { results: OperatorPositionChangeRequest[] }
      return { results: r.results || [], count: getNumberField(r, 'count') ?? 0 }
    }
    const arr = Array.isArray(resp) ? (resp as OperatorPositionChangeRequest[]) : []
    return { results: arr, count: arr.length }
  }

  /**
   * 获取变更申请详情
   * API: GET /api/v1/admin/position-change-requests/{id}/
   */
  async retrieve(id: string | number): Promise<OperatorPositionChangeRequest> {
    return await httpClient.get<OperatorPositionChangeRequest>(`admin/position-change-requests/${id}/`)
  }

  /**
   * 创建变更申请
   * API: POST /api/v1/admin/position-change-requests/
   */
  async create(data: {
    target_employee: string | number
    change_type: string
    target_department: string | number
    target_position: string | number
    assignment_type?: string
    effective_from?: string
    effective_until?: string | null
    reason?: string
  }): Promise<OperatorPositionChangeRequest> {
    return await httpClient.post<OperatorPositionChangeRequest>('admin/position-change-requests/', data)
  }

  /**
   * 更新变更申请
   * API: PATCH /api/v1/admin/position-change-requests/{id}/
   */
  async patch(id: string | number, data: Partial<OperatorPositionChangeRequest>): Promise<OperatorPositionChangeRequest> {
    return await httpClient.patch<OperatorPositionChangeRequest>(`admin/position-change-requests/${id}/`, data)
  }

  /**
   * 删除变更申请
   * API: DELETE /api/v1/admin/position-change-requests/{id}/
   */
  async delete(id: string | number): Promise<void> {
    await httpClient.delete<unknown>(`admin/position-change-requests/${id}/`)
  }

  /**
   * 审批变更申请（approve/reject）
   * API: POST /api/v1/admin/position-change-requests/{id}/approve/
   */
  async approve(id: string | number, data: { action: 'approve' | 'reject'; approval_notes?: string }): Promise<unknown> {
    return await httpClient.post<unknown>(`admin/position-change-requests/${id}/approve/`, data)
  }

  /**
   * 获取待审批列表
   * API: GET /api/v1/admin/position-change-requests/pending_approvals/
   */
  async pendingApprovals(): Promise<unknown> {
    return await httpClient.get<unknown>('admin/position-change-requests/pending_approvals/')
  }

  /**
   * 获取变更申请统计
   * API: GET /api/v1/admin/position-change-requests/statistics/
   */
  async statistics(): Promise<unknown> {
    return await httpClient.get<unknown>('admin/position-change-requests/statistics/')
  }
}

/**
 * 运营商员工职级分配（OperatorEmployeePosition）接口
 * API: /api/v1/admin/employee-positions/
 */
export interface OperatorEmployeePosition {
  id: string | number
  employee: string | number
  employee_name?: string
  full_name?: string
  phone?: string
  department: string | number
  department_name?: string
  position: string | number
  position_name?: string
  position_level?: string
  position_category?: string
  assignment_type?: string
  status?: string
  effective_from?: string
  effective_until?: string | null
  created_at?: string
  updated_at?: string
}

export class OperatorEmployeePositionsApiService {
  async list(params?: Record<string, unknown>): Promise<unknown> {
    return await httpClient.get<unknown>('admin/employee-positions/', { params })
  }

  async create(data: {
    employee: string | number
    department: string | number
    position: string | number
    assignment_type?: string
    status?: string
    effective_from?: string
    effective_until?: string | null
    notes?: string
  }): Promise<OperatorEmployeePosition> {
    return await httpClient.post<OperatorEmployeePosition>('admin/employee-positions/', data)
  }

  async patch(id: string | number, data: Partial<OperatorEmployeePosition>): Promise<OperatorEmployeePosition> {
    return await httpClient.patch<OperatorEmployeePosition>(`admin/employee-positions/${id}/`, data)
  }

  async delete(id: string | number): Promise<void> {
    await httpClient.delete(`admin/employee-positions/${id}/`)
  }

  /**
   * 按部门查看职级分布
   * API: GET /api/v1/admin/employee-positions/department_positions/
   */
  async departmentPositions(params?: Record<string, unknown>): Promise<unknown> {
    return await httpClient.get<unknown>('admin/employee-positions/department_positions/', { params })
  }

  /**
   * 我的职级
   * API: GET /api/v1/admin/employee-positions/my_positions/
   */
  async myPositions(): Promise<unknown> {
    return await httpClient.get<unknown>('admin/employee-positions/my_positions/')
  }
}

/**
 * 租户员工 API
 * ⚠️ 注意：使用 /api/v1/users/ 并过滤 user_type 为运营商类型
 * user_type 可选值：OPERATOR, OPERATOR_ADMIN
 */
export class TenantStaffApiService {
  /**
   * 获取员工列表（包含职级信息）
   * API: GET /api/v1/admin/employee-positions/?department={id}
   * 
   * 重要：使用 OperatorEmployeePosition API 而不是 User API
   * 原因：需要获取员工的职级、部门等完整信息
   */
  async getStaffList(params?: {
    department_id?: string | number
    department?: string | number  // ← 后端参数名是 department，不是 department_id
    employee?: string | number    // ✅ 按员工(User) ID 精确过滤（用于企业表单自动回填上级职级）
    include_unassigned?: boolean
    page?: number
    page_size?: number
    search?: string
  }): Promise<{ results: TenantStaff[]; count: number; total: number; page: number; page_size: number }> {
    // ✅ 使用 admin/employee-positions API（包含职级信息）
    const queryParams: Record<string, unknown> = { ...(params || {}) }
    
    // ✅ 参数名映射：department_id → department
    if (queryParams.department_id) {
      queryParams.department = queryParams.department_id
      delete queryParams.department_id
    }
    
    console.debug('[TenantStaffApi] getStaffList request:', { params: queryParams })
    
    const response = await httpClient.get<unknown>('admin/employee-positions/', { params: queryParams })
    
    console.debug('[TenantStaffApi] getStaffList raw response:', response)
    
    // 处理分页响应并映射字段名
    if (isRecord(response) && 'results' in response) {
      const resp = response as Record<string, unknown>
      const rawResults = Array.isArray(resp.results) ? (resp.results as unknown[]) : []
      // ✅ 字段映射：后端 → 前端
      // ⚠️ 重要口径：
      // - 后端 admin/employee-positions 返回的 item.id 是 OperatorEmployeePosition.id（职级分配记录ID）
      // - 创建企业等业务字段 operation_manager_id/supervisor_id/customer_service_id 需要的是“员工账号(User) ID”
      // 因此前端选择器必须使用 item.employee 作为“员工ID”，避免提交职级记录ID导致 400 无效主键。
      const mappedResults = rawResults.map((item) => {
        const it = isRecord(item) ? (item as Record<string, unknown>) : {}
        const employee = toId(it.employee)
        const department = toId(it.department)
        const position = toId(it.position)
        const employeeName = String(it.employee_name || it.username || '')
        const fullName = String(it.full_name || employeeName || '')
        const deptName = String(it.department_name || '-')
        const posName = String(it.position_name || '-')
        const createdAt = String(it.created_at || '')
        const updatedAt = String(it.updated_at || '')
        const isActive = String(it.status || '').toUpperCase() !== 'INACTIVE'
        const empCode = String((it as Record<string, unknown>).employee_code || '')

        const out = {
        // 保留原字段（含 employee/department/position 等）
        ...(it as Record<string, unknown>),
        // ✅ 选择器/业务提交使用的“员工ID”
        id: employee,
        username: employeeName || String(employee),
        // ✅ 保留职级记录ID，便于追溯/展示（若未来需要）
        employee_position_id: toId(it.id),
        // ✅ 添加前端期望的字段（优先使用 full_name）
        display_name: fullName || '-',
        employee_code: empCode || '',
        department: { id: department, name: deptName },
        department_id: department,
        position_id: position,
        position: posName,
        // ✅ 确保 full_name 字段可用
        full_name: fullName || '-',
        operator_manager: toId(it.operator_manager),
        operator_manager_name: String(it.operator_manager_name || ''),
        is_active: isActive,
        created_at: createdAt,
        updated_at: updatedAt
        }
        return out as unknown as TenantStaff
      })
      
      console.debug('[TenantStaffApi] getStaffList mapped results count:', mappedResults.length)
      
      return {
        results: mappedResults,
        count: getNumberField(resp, 'count') ?? getNumberField(resp, 'total') ?? 0,
        total: getNumberField(resp, 'count') ?? getNumberField(resp, 'total') ?? 0,
        page: getNumberField(resp, 'page') ?? (params?.page || 1),
        page_size: getNumberField(resp, 'page_size') ?? (params?.page_size || 20)
      }
    }
    
    // 非分页响应（可能是数组或其他格式）
    console.debug('[TenantStaffApi] getStaffList non-paginated response, type:', typeof response, Array.isArray(response))
    
    const results = Array.isArray(response) ? response : []
    return {
      results: results as TenantStaff[],
      count: results.length,
      total: results.length,
      page: 1,
      page_size: results.length
    }
  }

  /**
   * 获取员工详情
   * API: GET /api/v1/users/{pk}/
   * 
   * 注意：后端 lookup_field = "pk"，使用用户 ID
   */
  async getStaff(id: string | number): Promise<TenantStaff> {
    return await httpClient.get<TenantStaff>(`users/${id}/`)
  }

  /**
   * 创建运营商员工
   * API: POST /api/v1/users/
   * 
   * 必填字段：
   * - username: 用户名
   * - first_name: 显示名称
   * - user_type: 用户类型（运营商员工填 'operator'）
   * - operator_department: 部门 ID（可选）
   */
  async createStaff(data: {
    username?: string
    first_name?: string
    phone_number?: string
    email?: string
    operator_department?: string | number
    user_type?: string
    status?: string
    password?: string
    position_id?: number
    assignment_type?: string
  }): Promise<TenantStaff> {
    return await httpClient.post<TenantStaff>('users/', data)
  }

  /**
   * 更新运营商员工
   * API: PATCH /api/v1/users/{pk}/
   */
  async updateStaff(id: string | number, data: Partial<{
    first_name: string
    phone_number: string
    email: string
    operator_department: string | number
    status: string
  }>): Promise<TenantStaff> {
    return await httpClient.patch<TenantStaff>(`users/${id}/`, data)
  }

  /**
   * 删除员工
   * API: DELETE /api/v1/users/{pk}/
   */
  async deleteStaff(id: string | number): Promise<void> {
    await httpClient.delete(`users/${id}/`)
  }
}

export const operatorPositionsApi = new OperatorPositionsApiService()
export const operatorEmployeePositionsApi = new OperatorEmployeePositionsApiService()
export const operatorPositionChangeRequestsApi = new OperatorPositionChangeRequestsApiService()

// ==================== 子运营商管理 API ====================

/**
 * 子运营商数据接口
 * 对齐后端 SubOperatorManagementSerializer（tenants/serializers.py）
 */
export interface SubOperator {
  [key: string]: unknown
  id: string
  name: string
  tenant_type?: string
  tenant_type_display?: string
  status?: string
  status_display?: string
  parent_tenant?: string | number
  parent_tenant_name?: string
  business_license?: string
  contact_person?: string
  contact_phone?: string
  contact_email?: string
  address?: string
  cooperation_mode?: string
  commission_rate?: string
  service_fee_rate?: string
  created_by?: string | number
  created_by_name?: string
  updated_by?: string | number
  updated_by_name?: string
  created_at?: string
  updated_at?: string
  total_users?: number
  total_merchants?: number
  total_enterprises?: number
  total_shared_resources?: number
  total_orders?: number
}

export interface SubOperatorDashboard {
  today_orders?: number
  today_revenue?: string
  month_orders?: number
  month_revenue?: string
  total_merchants?: number
  total_enterprises?: number
  shared_resources?: number
  license_warning?: boolean
  [key: string]: unknown
}

export interface SubOperatorStatistics {
  total?: number
  active_count?: number
  inactive_count?: number
  cooperation_mode_distribution?: Record<string, number>
  [key: string]: unknown
}

class SubOperatorApiService {
  /**
   * 子运营商列表
   * API: GET /api/v1/admin/sub-operators/
   */
  async list(params?: Record<string, unknown>): Promise<{ results: SubOperator[]; count: number; total?: number }> {
    const resp = await httpClient.get<unknown>('admin/sub-operators/', { params })
    if (isRecord(resp)) {
      return {
        results: Array.isArray(resp.results) ? resp.results as SubOperator[] : [],
        count: typeof resp.count === 'number' ? resp.count : 0,
        total: typeof resp.total === 'number' ? resp.total : undefined,
      }
    }
    return { results: [], count: 0 }
  }

  /**
   * 子运营商详情
   * API: GET /api/v1/admin/sub-operators/{id}/
   */
  async retrieve(id: string | number): Promise<SubOperator> {
    return await httpClient.get<SubOperator>(`admin/sub-operators/${id}/`)
  }

  /**
   * 创建子运营商
   * API: POST /api/v1/admin/sub-operators/
   */
  async create(data: Partial<SubOperator>): Promise<SubOperator> {
    return await httpClient.post<SubOperator>('admin/sub-operators/', data)
  }

  /**
   * 更新子运营商
   * API: PATCH /api/v1/admin/sub-operators/{id}/
   */
  async update(id: string | number, data: Partial<SubOperator>): Promise<SubOperator> {
    return await httpClient.patch<SubOperator>(`admin/sub-operators/${id}/`, data)
  }

  /**
   * 删除子运营商
   * API: DELETE /api/v1/admin/sub-operators/{id}/
   */
  async delete(id: string | number): Promise<void> {
    await httpClient.delete(`admin/sub-operators/${id}/`)
  }

  /**
   * 激活子运营商
   * API: POST /api/v1/admin/sub-operators/{id}/activate/
   */
  async activate(id: string | number, data?: Record<string, unknown>): Promise<unknown> {
    return await httpClient.post<unknown>(`admin/sub-operators/${id}/activate/`, data ?? {})
  }

  /**
   * 停用子运营商
   * API: POST /api/v1/admin/sub-operators/{id}/deactivate/
   */
  async deactivate(id: string | number, data: { reason: string }): Promise<unknown> {
    return await httpClient.post<unknown>(`admin/sub-operators/${id}/deactivate/`, data)
  }

  /**
   * 子运营商仪表盘
   * API: GET /api/v1/admin/sub-operators/{id}/dashboard/
   */
  async dashboard(id: string | number): Promise<SubOperatorDashboard> {
    return await httpClient.get<SubOperatorDashboard>(`admin/sub-operators/${id}/dashboard/`)
  }

  /**
   * 子运营商统计
   * API: GET /api/v1/admin/sub-operators/statistics/
   */
  async statistics(): Promise<SubOperatorStatistics> {
    return await httpClient.get<SubOperatorStatistics>('admin/sub-operators/statistics/')
  }
}

export const subOperatorApi = new SubOperatorApiService()

// 导出单例
export const tenantDepartmentsApi = new TenantDepartmentsApiService()
export const tenantStaffApi = new TenantStaffApiService()

// 运营城市 API 便捷导出
export const operatorCitiesApi = {
  list: listOperatorCities,
  get: getOperatorCity
}

export default {
  departments: tenantDepartmentsApi,
  staff: tenantStaffApi,
  cities: operatorCitiesApi,
  positions: operatorPositionsApi,
  positionChangeRequests: operatorPositionChangeRequestsApi,
  subOperators: subOperatorApi
}

